import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, ArrowLeft, UserCheck, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { track } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RegisterPage() {
  const { register, login: authLogin } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [guestLoading, setGuestLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Fire `signup_started` once when the user reaches the registration form,
  // regardless of which CTA / direct URL brought them here. Guard against
  // React 18 StrictMode double-invoke and Google OAuth callback (hash present
  // means we are returning from Google and AuthCallback is handling it).
  React.useEffect(() => {
    if (window.location.hash?.includes('session_id=')) return;
    if (sessionStorage.getItem('mp_signup_started_fired') === '1') return;
    sessionStorage.setItem('mp_signup_started_fired', '1');
    track('signup_started');
  }, []);

  const friendlyValidationError = (error) => {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0];
      const field = (first.loc || []).slice(-1)[0];
      if (field === 'email') {
        return language === 'fr'
          ? 'Adresse e-mail invalide.'
          : language === 'es' ? 'Dirección de correo inválida.'
          : language === 'de' ? 'Ungültige E-Mail-Adresse.'
          : language === 'pt' ? 'Endereço de e-mail inválido.'
          : 'Invalid email address.';
      }
      if (field === 'password') {
        return language === 'fr'
          ? 'Mot de passe trop court (minimum 6 caractères).'
          : language === 'es' ? 'Contraseña demasiado corta (mínimo 6 caracteres).'
          : language === 'de' ? 'Passwort zu kurz (mindestens 6 Zeichen).'
          : language === 'pt' ? 'Senha demasiado curta (mínimo 6 caracteres).'
          : 'Password too short (minimum 6 characters).';
      }
      return first.msg || t('error');
    }
    return t('error');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Backend auto-generates a `user_XXXX` username when none is provided.
      await register(formData.email, formData.password);
      toast.success(t('success'));
      navigate('/select-partner');
    } catch (error) {
      console.error('Register error:', error);
      toast.error(friendlyValidationError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleGuestMode = async () => {
    setGuestLoading(true);
    try {
      const response = await axios.post(`${API}/auth/guest`);
      // Await so the token/user are persisted (localStorage) before we
      // navigate. Prevents a race where the next page renders before the
      // auth state is ready and bounces the user back to /login.
      await authLogin(response.data.access_token, response.data.user);
      navigate('/select-gender');
    } catch (error) {
      console.error('Guest login error:', error);
      if (error?.code === 'STORAGE_UNAVAILABLE') {
        // Safari private mode, disabled cookies/storage, quota exceeded…
        toast.error(
          language === 'fr'
            ? "Impossible d'enregistrer ta session. Désactive le mode navigation privée ou autorise le stockage local, puis réessaie."
            : language === 'es'
              ? 'No se pudo guardar tu sesión. Desactiva el modo privado o permite el almacenamiento local e inténtalo de nuevo.'
              : language === 'de'
                ? 'Sitzung konnte nicht gespeichert werden. Deaktiviere den privaten Modus oder erlaube lokalen Speicher und versuche es erneut.'
                : language === 'pt'
                  ? 'Não foi possível guardar a sessão. Desativa a navegação privada ou permite o armazenamento local e tenta novamente.'
                  : "Couldn't save your session. Please disable private browsing or allow local storage, then try again."
        );
      } else {
        toast.error(language === 'fr' ? 'Erreur de connexion invité' : 'Guest login error');
      }
    } finally {
      setGuestLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-velvet-bg flex items-center justify-center px-4 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-velvet-bg via-velvet-bg to-purple-dark/20" />
      <div className="absolute top-1/4 -right-32 w-64 h-64 bg-purple-main/20 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 -left-32 w-64 h-64 bg-fuchsia-main/10 rounded-full blur-3xl" />
      
      <div className="relative z-10 w-full max-w-md">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-velvet_text-secondary hover:text-velvet_text-accent mb-8 transition-colors"
          data-testid="back-to-home-link"
        >
          <ArrowLeft size={20} />
          {t('back')}
        </Link>
        
        <div className="glass rounded-2xl p-8 animate-slideUp">
          <h1 className="font-heading text-3xl text-velvet_text-primary mb-2">{t('register_title')}</h1>
          <p className="text-velvet_text-secondary mb-8">{t('register_subtitle')}</p>
          
          {/* Guest Mode — promoted as the primary easy-entry option */}
          <button
            type="button"
            onClick={handleGuestMode}
            disabled={guestLoading}
            className="w-full flex items-center justify-center gap-2 h-12 bg-terracotta-light/15 hover:bg-terracotta-light/25 border border-terracotta-main/40 rounded-full text-terracotta-dark font-medium transition-colors"
            data-testid="guest-register-button"
          >
            {guestLoading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <UserCheck size={18} />
            )}
            {language === 'fr' ? 'Continuer en mode invité' : 'Continue as guest'}
          </button>

          {/* "ou" separator between Guest and Google */}
          <div className="relative my-3">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-velvet-paper px-3 text-velvet_text-secondary">
                {language === 'fr' ? 'ou' : 'or'}
              </span>
            </div>
          </div>

          {/* Google Login Button */}
          <button
            type="button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + '/register';
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full flex items-center justify-center gap-3 h-12 rounded-full border border-white/20 bg-white/5 hover:bg-white/10 text-velvet_text-primary font-medium transition-colors"
            data-testid="google-register-button"
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {language === 'fr' ? 'Continuer avec Google' : 'Continue with Google'}
          </button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-velvet-paper px-3 text-velvet_text-secondary">
                {language === 'fr' ? 'ou' : 'or'}
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-velvet_text-secondary">{t('email')}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-velvet-paper border-white/10 text-velvet_text-primary h-12"
                placeholder={t('email_placeholder')}
                required
                data-testid="register-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-velvet_text-secondary">{t('password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="bg-velvet-paper border-white/10 text-velvet_text-primary h-12 pr-12"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-velvet_text-secondary hover:text-velvet_text-accent transition-colors"
                  data-testid="register-toggle-password"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            
            <div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-purple-main hover:bg-purple-dark text-white rounded-full h-12 font-semibold shadow-neon"
                data-testid="register-submit-button"
              >
                {loading ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  t('register')
                )}
              </Button>
              <p
                className="text-center text-xs text-velvet_text-secondary/70 mt-3"
                data-testid="no-card-required-note"
              >
                {t('no_card_required')}
              </p>
            </div>
          </form>
          
          <p className="text-center mt-6 text-velvet_text-secondary">
            {t('have_account')}{' '}
            <Link 
              to="/login" 
              className="text-velvet_text-accent hover:text-purple-light transition-colors"
              data-testid="login-link"
            >
              {t('login')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
